import React, { useState } from 'react'
import PropTypes from 'prop-types'
import { Link as RouterLink } from 'react-router-dom'
import ReactMarkdown from 'react-markdown'
import { DateTime } from 'luxon'
import Avatar from '@material-ui/core/Avatar'
import IconButton from '@material-ui/core/IconButton'
import Typography from '@material-ui/core/Typography'
import CalendarTodayIcon from 'components/material-symbol/icons/CalendarToday'
import ScheduleIcon from 'components/material-symbol/icons/Schedule'
import LocationOnIcon from 'components/material-symbol/icons/LocationOn'
import ChatBubbleIcon from 'components/material-symbol/icons/ChatBubble'
import ExpandMoreIcon from 'components/material-symbol/icons/ExpandMore'
import ExpandLessIcon from 'components/material-symbol/icons/ExpandLess'
import Chip from '@material-ui/core/Chip'
import { PGA_COM_HOST } from 'env'
import { composeOptimizedImageUrl } from 'lib/utils'
import pluralize from 'lib/pluralize'
import { getInitials } from 'lib/text-display'
import { COACHING_CENTER_SLUGS } from 'utils/constants'
import { isRecurringPayment } from 'utils/groupEventUtils'
import { CANCELLATION_POLICY } from '../../constants'
import PriceSummary from './price-summary'
import useStyles from './styles'

const MAX_SPOTS_TO_SHOW = 10
const FRISCO_COACHING_CENTER_SLUG = 'pga-coaching-center-at-pga-frisco'

const formatDateRow = ({ groupEvent, start }) => {
  const sameYear =
    start.year === DateTime.now().setZone(groupEvent.timezone).year
  const date = start.toFormat(sameYear ? 'ccc, LLL d' : 'ccc, LLL d, yyyy')
  const hasMultipleSessions =
    isRecurringPayment(groupEvent) || groupEvent.lessonCount > 1
  const parts = [`${hasMultipleSessions ? 'Starts ' : ''}${date}`]
  if (groupEvent.lessonCount > 1) {
    parts.push(`${groupEvent.lessonCount} sessions`)
  }
  return parts.join(' · ')
}

const getSpotsRemaining = groupEvent => {
  const {
    maxAttendance,
    registrationCount,
    isCanceled,
    registrationClosed,
    registrationFull,
  } = groupEvent
  const isRegistrationEnabled = !(
    isCanceled ||
    registrationClosed ||
    registrationFull
  )
  const spotsRemaining =
    maxAttendance != null && maxAttendance - registrationCount
  return isRegistrationEnabled &&
    spotsRemaining > 0 &&
    spotsRemaining <= MAX_SPOTS_TO_SHOW
    ? spotsRemaining
    : null
}

// Mirrors when CoachLogo shows a crest: QE / no member type shows nothing,
// AP / ST always show, everything else needs a certification level.
const getMemberLabel = ({ memberType, certificationLevel }) => {
  if (!memberType || memberType === 'QE') return null
  if (memberType === 'AP') return 'PGA Associate'
  if (memberType === 'ST') return 'PGA Student'
  return certificationLevel ? 'PGA Member' : null
}

const EventSummaryCard = ({
  groupEvent,
  paymentRequired,
  setPaymentRequired,
  eyebrow,
  hidePrice,
}) => {
  const classes = useStyles()
  const isCoachingCenterGE = COACHING_CENTER_SLUGS.includes(
    groupEvent?.academy?.slug,
  )
  const [aboutOpen, setAboutOpen] = useState(isCoachingCenterGE)

  const {
    title,
    featuredImageUrl,
    timezone,
    coach,
    coachFacility,
    slug,
    description,
  } = groupEvent
  const start = DateTime.fromISO(groupEvent.startDateTime).setZone(timezone)
  const end = DateTime.fromISO(groupEvent.endDateTime).setZone(timezone)
  const spotsRemaining = getSpotsRemaining(groupEvent)
  const { title: coachTitle, profilePhoto, memberType, certificationLevel } =
    coach?.coachProfile || {}
  const memberLabel = getMemberLabel({ memberType, certificationLevel })
  const aboutText = isCoachingCenterGE
    ? `${description || ''}${CANCELLATION_POLICY}`
    : description
  const address = [coachFacility?.street, coachFacility?.city]
    .filter(Boolean)
    .join(', ')

  return (
    <section className={classes.summary} aria-label="Event summary">
      <div
        className={classes.summaryImage}
        role="img"
        aria-label={title}
        style={
          featuredImageUrl
            ? {
              backgroundImage: `url("${composeOptimizedImageUrl(
                featuredImageUrl,
                800,
              )}")`,
            }
            : undefined
        }
      >
        {spotsRemaining && (
          <span className={classes.spotsPill}>
            {spotsRemaining} {pluralize(spotsRemaining, 'spot')} left
          </span>
        )}
      </div>
      <div className={classes.summaryBody}>
        <span className={classes.summaryEyebrow}>{eyebrow}</span>
        <Typography
          variant="h5"
          component="h2"
          className={classes.summaryTitle}
        >
          {title}
        </Typography>

        <div className={classes.factPair}>
          <div className={classes.factRow}>
            <CalendarTodayIcon aria-hidden="true" />
            <span>{formatDateRow({ groupEvent, start })}</span>
          </div>
          <div className={classes.factRow}>
            <ScheduleIcon aria-hidden="true" />
            <span>
              {start.toFormat('h:mm')} – {end.toFormat('h:mm a (ZZZZ)')}
            </span>
          </div>
        </div>
        <div className={classes.factRow}>
          <LocationOnIcon aria-hidden="true" />
          <span>
            {groupEvent.academy?.slug === FRISCO_COACHING_CENTER_SLUG ? (
              <a
                href={`${PGA_COM_HOST}/${FRISCO_COACHING_CENTER_SLUG}`}
                rel="noopener noreferrer"
              >
                {coachFacility?.name}
              </a>
            ) : (
              coachFacility?.name
            )}
            {address && (
              <span className={classes.factSecondary}>{address}</span>
            )}
          </span>
        </div>

        <hr className={classes.summaryDivider} />

        <div className={classes.coachRow}>
          <Avatar
            className={classes.coachAvatar}
            alt={coach?.name}
            src={profilePhoto}
          >
            {getInitials(coach?.name)}
          </Avatar>
          <div>
            <Typography
              variant="h6"
              component="p"
              className={classes.coachName}
            >
              {coach?.name}
            </Typography>
            {(memberLabel || coachTitle) && (
              <span className={classes.coachTitle}>
                {memberLabel && (
                  <Chip
                    size="small"
                    label={memberLabel}
                    className={classes.memberChip}
                  />
                )}
                {coachTitle}
              </span>
            )}
          </div>
          <IconButton
            component={RouterLink}
            to={`/things-to-do/events/${slug}/messages`}
            className={classes.messageButton}
            aria-label="Message event host"
          >
            <ChatBubbleIcon />
          </IconButton>
        </div>

        {!hidePrice && (
          <>
            <hr className={classes.summaryDivider} />
            <PriceSummary
              groupEvent={groupEvent}
              paymentRequired={paymentRequired}
              setPaymentRequired={setPaymentRequired}
            />
          </>
        )}

        {aboutText && (
          <>
            <hr className={classes.summaryDivider} />
            <button
              type="button"
              className={classes.aboutToggle}
              aria-expanded={aboutOpen}
              aria-controls="registration-about-event"
              onClick={() => setAboutOpen(open => !open)}
            >
              About this event
              {aboutOpen ? <ExpandLessIcon /> : <ExpandMoreIcon />}
            </button>
            {aboutOpen && (
              <div id="registration-about-event" className={classes.aboutBody}>
                <ReactMarkdown>{aboutText}</ReactMarkdown>
              </div>
            )}
          </>
        )}
      </div>
    </section>
  )
}

EventSummaryCard.propTypes = {
  groupEvent: PropTypes.object.isRequired,
  paymentRequired: PropTypes.bool,
  setPaymentRequired: PropTypes.func,
  // PGA HOPE registration reuses the card: its own eyebrow, and no price block
  // because that flow takes no payment.
  eyebrow: PropTypes.string,
  hidePrice: PropTypes.bool,
}

EventSummaryCard.defaultProps = {
  eyebrow: 'GROUP EVENT',
  hidePrice: false,
}

export default EventSummaryCard
